// ─────────────────────────────────────────────────────────────────────────────
// TodaysPlanCard
//
// Dashboard widget showing rule status at a glance.
// Reads the user's rules for the current account, evaluates them against
// today's live context, and displays compliance status.
//
// Three visual states:
//   - On Plan: all rules satisfied (green headline)
//   - Take Caution: at least one rule violated (yellow headline)
//   - No rules: shows a CTA to configure rules
//
// News blackout is fed from the dashboard's shared useCalendar state
// (todayEvents + calendarTrustworthy). Previously news_events was hard-coded
// to [], so the rule could never fire. If the calendar data can't be
// trusted, the news rule is shown as violated with a "can't verify" reason
// rather than a green check — absence of data is not absence of events.
//
// All times are ET (the rule evaluator compares HH:MM:SS strings, and the
// calendar is ET), so the clock uses America/New_York, not browser-local.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../ui/Card";
import { C } from "../../lib/colors";
import { useUserRules } from "../../hooks/useUserRules";
import {
  evaluateAllRules,
  buildLiveContext,
} from "../../lib/rules";

export function TodaysPlanCard({
  userId,
  accountId,
  accountTrades = [],
  todayEvents = [],
  calendarTrustworthy = false,
}) {
  const navigate = useNavigate();
  const { rules, loading } = useUserRules(userId, accountId);

  // Tick every minute so blackouts and news countdowns stay accurate
  const [now, setNow] = useState(() => currentTimeStr());
  useEffect(() => {
    const id = setInterval(() => setNow(currentTimeStr()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Calendar events → rule-evaluator shape: { title, date, time } in ET
  const newsEvents = useMemo(
    () => (todayEvents || [])
      .filter(e => e?.dt && !e.isAllDay)
      .map(e => ({
        title:  e.event,
        date:   etDateStr(e.dt),
        time:   etTimeStr(e.dt),
        impact: e.impact,
      })),
    [todayEvents]
  );

  // Build the evaluation context
  const context = useMemo(() => buildLiveContext({
    trades: accountTrades,
    now,
    today_date: activeSessionDate(),
    news_events: newsEvents,
  }), [accountTrades, now, newsEvents]);

  // Evaluate rules
  const enabledRules = useMemo(
    () => (rules || []).filter(r => r.enabled),
    [rules]
  );
  const evaluations = useMemo(() => {
    const evals = evaluateAllRules(enabledRules, context);
    if (calendarTrustworthy) return evals;
    // Calendar untrusted: the news rule cannot assert "no blackout".
    const patched = evals.map(e => {
      if (e.rule?.rule_type !== "news_blackout" || !e.result || e.result.violated) return e;
      return {
        ...e,
        result: {
          ...e.result,
          violated: true,
          reason: "Can't verify — calendar data unavailable. Check news externally.",
        },
      };
    });
    // Keep evaluator's ordering contract: violated first
    return patched.sort((a, b) => (b.result?.violated ? 1 : 0) - (a.result?.violated ? 1 : 0));
  }, [enabledRules, context, calendarTrustworthy]);

  if (loading) {
    return (
      <Card style={{ padding: 18 }}>
        <SectionHeader title="Today's Plan" />
        <div style={{ color: C.muted, fontSize: 12, padding: "8px 0" }}>
          Loading rules…
        </div>
      </Card>
    );
  }

  // "All Accounts" view: rules don't apply globally — they're per-account.
  // Show a clean message explaining this instead of an empty widget.
  if (!accountId) {
    return (
      <Card style={{ padding: 18 }}>
        <SectionHeader title="Today's Plan" />
        <div style={{ padding: "12px 0 4px" }}>
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.5 }}>
            Trading rules are configured per account. Switch to a specific
            account above to see today's rule status.
          </div>
        </div>
      </Card>
    );
  }

  // No rules configured: gentle nudge
  if (enabledRules.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <SectionHeader title="Today's Plan" />
        <div style={{ padding: "12px 0 4px" }}>
          <div style={{ fontSize: 13, color: C.sub, marginBottom: 12, lineHeight: 1.5 }}>
            Set the rules you want to trade by for this account — daily caps, blackouts,
            news avoidance — and we'll show you where you stand each day.
          </div>
          <button
            onClick={() => navigate("/settings/rules")}
            style={{
              padding: "10px 16px",
              background: C.blue,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Set up rules →
          </button>
        </div>
      </Card>
    );
  }

  // Count violations
  const violations = evaluations.filter(e => e.result?.violated);
  const isOnPlan = violations.length === 0;

  return (
    <Card style={{ padding: 18 }}>
      <SectionHeader
        title="Today's Plan"
        right={now + " ET"}
        onClick={() => navigate("/settings/rules")}
      />

      <StatusHeadline
        isOnPlan={isOnPlan}
        totalRules={enabledRules.length}
        violationsCount={violations.length}
      />

      {/* Per-rule headlines */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
        {evaluations.map(e => (
          <RuleHeadlineRow key={e.rule.id} evaluation={e} />
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function SectionHeader({ title, right, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{
        fontSize: 12, color: C.muted, letterSpacing: "0.08em",
        fontWeight: 700, textTransform: "uppercase",
      }}>
        {title}
      </div>
      {right && (
        <div style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
          {right}
        </div>
      )}
    </div>
  );
}

function StatusHeadline({ isOnPlan, totalRules, violationsCount }) {
  const color = isOnPlan ? C.green : C.yellow;
  const bg = isOnPlan ? C.green + "15" : C.yellow + "15";
  const icon = isOnPlan ? "✓" : "⚠";
  const headline = isOnPlan ? "ON PLAN" : "TAKE CAUTION";
  const subtitle = isOnPlan
    ? `${totalRules} of ${totalRules} rules satisfied`
    : `${violationsCount} of ${totalRules} rule${violationsCount === 1 ? "" : "s"} violated`;

  return (
    <div style={{
      marginTop: 14,
      padding: "16px 18px",
      background: bg,
      border: `1px solid ${color}55`,
      borderRadius: 10,
      display: "flex",
      gap: 14,
      alignItems: "center",
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: "50%",
        background: color + "33",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, color, fontWeight: 800,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color, letterSpacing: "0.04em" }}>
          {headline}
        </div>
        <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function RuleHeadlineRow({ evaluation }) {
  const { rule, def, result } = evaluation;
  if (!def || !result) return null;

  const violated = result.violated;
  const color = violated ? C.red : C.green;
  const bg = violated ? C.red + "0a" : "rgba(255,255,255,0.02)";

  // Get a custom headline if the rule type provides one
  const headline = def.headline
    ? def.headline(rule, evaluation.def === def && evaluation.result ? buildContextForHeadline(rule, evaluation) : {})
    : null;

  const labelText = rule.custom_label || def.short_label || def.label;

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "28px 1fr auto auto",
      gap: 10,
      alignItems: "center",
      padding: "10px 12px",
      background: bg,
      border: `1px solid ${violated ? color + "33" : C.border}`,
      borderRadius: 8,
      opacity: rule.severity === "silent" ? 0.7 : 1,
    }}>
      <span style={{ fontSize: 16 }}>{def.icon}</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {labelText}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
          {result.reason}
        </div>
      </div>
      {headline?.current !== undefined && headline?.limit !== undefined && (
        <ProgressDots current={headline.current} limit={Math.abs(headline.limit)} color={color} />
      )}
      <span style={{
        fontSize: 14, color, fontWeight: 700, minWidth: 18, textAlign: "center",
      }}>
        {violated ? "✕" : "✓"}
      </span>
    </div>
  );
}

function ProgressDots({ current, limit, color }) {
  // For numeric counts (trades, losses), show dots like ●●●○○○○○
  if (typeof current !== "number" || typeof limit !== "number" || limit <= 0) return null;

  // Cap visual at 10 dots — show ratio if larger
  const showRatio = limit > 10;
  if (showRatio) {
    return (
      <span style={{
        fontSize: 11, color: C.muted, fontFamily: "monospace",
        whiteSpace: "nowrap",
      }}>
        {current} / {limit}
      </span>
    );
  }

  const dots = [];
  for (let i = 0; i < limit; i++) {
    dots.push(
      <span key={i} style={{
        fontSize: 8,
        color: i < current ? color : C.border,
      }}>●</span>
    );
  }
  return (
    <span style={{ whiteSpace: "nowrap", letterSpacing: 2 }}>
      {dots}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a partial context object so headline() can compute its values
 * without re-running the whole evaluator.
 */
function buildContextForHeadline(rule, evaluation) {
  // The original evaluator already built a context for check() — we use
  // the same shape so headline() gets the same data.
  // Since we don't have direct access to the original ctx, we just return
  // what the rule's check() saw, derived from result.detail.
  return evaluation.result?.detail || {};
}

/**
 * Current ET time as "HH:MM:SS". Rules (blackouts, trade window, news) are
 * defined in ET, so evaluating against browser-local time was wrong for any
 * user outside Eastern.
 */
function currentTimeStr() {
  return etTimeStr(new Date());
}

/** "HH:MM:SS" for a Date, in ET. */
function etTimeStr(d) {
  return d.toLocaleTimeString("en-GB", {
    timeZone: "America/New_York", hour12: false,
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

/** "YYYY-MM-DD" for a Date, in ET. */
function etDateStr(d) {
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

/**
 * Active CME session date — "today" follows the CME convention:
 *   - Before 6 PM ET → today's calendar date
 *   - At/after 6 PM ET → tomorrow's date (next session has opened)
 *
 * Returns "YYYY-MM-DD".
 *
 * NOTE: This duplicates the helper from lib/format.js so this widget
 * stays self-contained. If you have the helper exported, replace this
 * with an import.
 */
function activeSessionDate() {
  const now = new Date();
  // Convert to ET (UTC-5/-4 depending on DST — Date handles it via the locale)
  const etNow = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const hour = etNow.getHours();
  // After 6 PM ET → next session
  if (hour >= 18) {
    etNow.setDate(etNow.getDate() + 1);
  }
  const y = etNow.getFullYear();
  const m = String(etNow.getMonth() + 1).padStart(2, "0");
  const d = String(etNow.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
